import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./LegalPages.css";

function TermsAndConditions() {
  const navigate = useNavigate();

  return (
    <div className="legal-page">
      <div className="legal-header">
        <Button 
          variant="outline" 
          onClick={() => navigate('/')}
          className="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>

      <div className="legal-content">
        <h1>STEP AND SIT - PLATFORM TERMS & CONDITIONS</h1>
        
        <div className="legal-meta">
          <p><strong>Effective Date:</strong> 01 March 2026</p>
          <p><strong>Last Updated:</strong> 01 March 2026</p>
        </div>

        <p className="legal-intro">
          These Terms & Conditions ("Terms") constitute a legally binding agreement between:
        </p>
        <ul>
          <li><strong>End Customers / Users ("Users")</strong></li>
          <li><strong>Salon / Grooming Centers ("Service Providers")</strong></li>
        </ul>
        <p>and</p>
        <p>
          <strong>Step and Sit LLP</strong>, a Limited Liability Partnership incorporated under the laws of India 
          ("Step and Sit", "Platform", "Company", "We", "Us", "Our").
        </p>
        <p>
          By accessing, downloading, registering, or using the Step and Sit application, website, or services 
          ("Platform"), you agree to these Terms.
        </p>
        <p><strong>If you do not agree, you must discontinue use immediately.</strong></p>

        <section>
          <h2>1. Platform Nature & Role</h2>
          <p>
            Step and Sit operates solely as a <strong>technology intermediary and booking facilitation marketplace</strong> 
            connecting Users with independent Service Providers.
          </p>
          <p>Step and Sit:</p>
          <ul>
            <li>Does not own or operate salons or grooming centers</li>
            <li>Does not employ Service Provider staff</li>
            <li>Does not supervise services</li>
            <li>Does not provide grooming or personal care services</li>
          </ul>
          <p>All services are independently rendered by Service Providers.</p>
        </section>

        <section>
          <h2>2. Eligibility</h2>
          <p>You confirm that:</p>
          <ul>
            <li>You are legally competent to contract under Indian law.</li>
            <li>Information submitted on the Platform is accurate and complete.</li>
          </ul>
        </section>

        <section>
          <h2>3. Service Responsibility</h2>
          <p>All services listed on the Platform are provided exclusively by Service Providers.</p>
          <p>Service Providers are solely responsible for:</p>
          <ul>
            <li>Service quality and execution</li>
            <li>Hygiene and safety standards</li>
            <li>Staff conduct</li>
            <li>Pricing and availability</li>
            <li>GST billing and statutory compliance</li>
            <li>Customer service handling</li>
          </ul>
          <p>Step and Sit shall not be liable for dissatisfaction, injury, allergic reactions, damages, or service outcomes.</p>
        </section>

        <section>
          <h2>4. Appointments, Delays & Operational Issues</h2>
          <p>Bookings may be affected due to:</p>
          <ul>
            <li>Staff unavailability</li>
            <li>Technical/system issues</li>
            <li>Traffic or weather conditions</li>
            <li>Power failures or emergencies</li>
            <li>Operational constraints</li>
          </ul>
          <p>
            Step and Sit bears no responsibility for delays, cancellations, or rescheduling caused by Service Providers 
            or external circumstances.
          </p>
        </section>

        <section>
          <h2>5. Arrival & No-Show Policy</h2>
          <p>Users agree to:</p>
          <ul>
            <li>Arrive at least <strong>15 minutes prior</strong> to appointment time.</li>
            <li>Arrival beyond <strong>5 minutes after scheduled time</strong> may be treated as a No-Show.</li>
          </ul>
          <p>Service Providers may enforce internal late-arrival policies.</p>
        </section>

        <section>
          <h2>6. Payments, Billing & GST</h2>
          <h3>Users</h3>
          <ul>
            <li>Payments are made directly to Service Providers.</li>
            <li>Step and Sit does not collect service payments or GST.</li>
            <li>Refunds are handled solely by Service Providers.</li>
          </ul>
          <h3>Service Providers</h3>
          <p>Service Providers are fully responsible for:</p>
          <ul>
            <li>Payment collection</li>
            <li>GST registration</li>
            <li>Tax compliance</li>
            <li>Invoice issuance</li>
          </ul>
        </section>

        <section>
          <h2>7. Platform Charges (Service Providers)</h2>
          <p>Step and Sit may charge:</p>
          <ul>
            <li>Subscription fees</li>
            <li>Commission</li>
            <li>Platform usage fees</li>
          </ul>
          <p>Failure to pay platform dues may result in suspension or termination.</p>
          <p>Step and Sit may revise charges with prior notice.</p>
        </section>

        <section>
          <h2>8. Products Sold at Salon</h2>
          <p>Products sold or recommended by Service Providers:</p>
          <ul>
            <li>Are not endorsed or sold by Step and Sit.</li>
            <li>Are used at customer discretion and risk.</li>
          </ul>
          <p>Users are advised to consult qualified professionals before product usage.</p>
        </section>

        <section>
          <h2>9. Medical Disclaimer</h2>
          <p>Services listed on the Platform:</p>
          <ul>
            <li>Are cosmetic or grooming services only.</li>
            <li>Do not constitute medical treatment or advice.</li>
          </ul>
          <p>Step and Sit provides no medical or dermatological consultation.</p>
        </section>

        <section>
          <h2>10. User Responsibilities</h2>
          <p>Users shall:</p>
          <ul>
            <li>Provide accurate information</li>
            <li>Maintain respectful conduct</li>
            <li>Follow salon policies</li>
            <li>Comply with hygiene requirements</li>
          </ul>
        </section>

        <section>
          <h2>11. Service Provider Responsibilities</h2>
          <p>Service Providers shall:</p>
          <ul>
            <li>Maintain professional and hygienic standards</li>
            <li>Deliver services as listed</li>
            <li>Avoid misleading pricing</li>
            <li>Comply with applicable laws</li>
          </ul>
        </section>

        <section>
          <h2>12. Limitation of Liability</h2>
          <p>To the fullest extent permitted by law, Step and Sit shall not be liable for:</p>
          <ul>
            <li>Service quality issues</li>
            <li>Personal injury or allergic reactions</li>
            <li>Product defects</li>
            <li>Operational failures of Service Providers</li>
            <li>Loss of profits or indirect damages</li>
          </ul>
          <p>The Platform acts only as an intermediary.</p>
        </section>

        <section>
          <h2>13. Indemnification</h2>
          <p>Service Providers agree to indemnify and hold harmless Step and Sit against claims arising from:</p>
          <ul>
            <li>Service execution</li>
            <li>Staff conduct</li>
            <li>Customer disputes</li>
            <li>Product usage</li>
            <li>Regulatory violations</li>
          </ul>
        </section>

        <section>
          <h2>14. Platform Misuse</h2>
          <p>Users and Service Providers shall not:</p>
          <ul>
            <li>Manipulate bookings</li>
            <li>Bypass the Platform</li>
            <li>Engage in fraud or abuse</li>
          </ul>
          <p>Violation may result in account termination.</p>
        </section>

        <section>
          <h2>15. Intellectual Property</h2>
          <p>All Platform software, branding, UI, logos, and content belong exclusively to Step and Sit LLP.</p>
          <p>Unauthorized reproduction or misuse is prohibited.</p>
        </section>

        <section>
          <h2>16. Electronic Records & Digital Consent</h2>
          <p>
            In accordance with the <strong>Information Technology Act, 2000</strong>, Users and Service Providers 
            acknowledge that:
          </p>
          <ul>
            <li>Acceptance of these Terms through electronic means constitutes valid legal consent.</li>
            <li>Digital acceptance shall be deemed equivalent to physical signature.</li>
            <li>Electronic records maintained by Step and Sit shall be admissible as legal evidence.</li>
          </ul>
        </section>

        <section>
          <h2>17. Force Majeure</h2>
          <p>
            Step and Sit shall not be liable for failure or delay in performance caused by events beyond reasonable 
            control, including but not limited to:
          </p>
          <ul>
            <li>Natural disasters</li>
            <li>Pandemic or epidemic</li>
            <li>Government restrictions</li>
            <li>Internet or telecom failures</li>
            <li>Cyber incidents</li>
            <li>Power outages</li>
            <li>War, riots, or civil disturbances</li>
          </ul>
        </section>

        <section>
          <h2>18. Suspension & Termination</h2>
          <p>Step and Sit may suspend or terminate access for:</p>
          <ul>
            <li>Fraud or misuse</li>
            <li>Policy violations</li>
            <li>Repeated complaints</li>
            <li>Non-payment of platform fees</li>
          </ul>
        </section>

        <section>
          <h2>19. Arbitration & Dispute Resolution</h2>
          <p>Any dispute arising out of or relating to these Terms shall be resolved through:</p>
          <ul>
            <li><strong>Good faith negotiation</strong>, failing which</li>
            <li><strong>Binding Arbitration</strong></li>
          </ul>
          <p>Arbitration shall:</p>
          <ul>
            <li>Be conducted under the <strong>Arbitration and Conciliation Act, 1996</strong></li>
            <li>Be handled by a <strong>sole arbitrator appointed by Step and Sit</strong></li>
            <li>Take place in <strong>Kannur, Kerala</strong></li>
            <li>Be conducted in English</li>
          </ul>
          <p>The arbitral award shall be final and binding.</p>
        </section>

        <section>
          <h2>20. Governing Law</h2>
          <p>These Terms shall be governed by the laws of India.</p>
          <p>Subject to arbitration, courts at <strong>Kannur, Kerala</strong> shall have jurisdiction.</p>
        </section>

        <section>
          <h2>21. Changes to Terms</h2>
          <p>Step and Sit reserves the right to modify these Terms at any time.</p>
          <p>Continued usage constitutes acceptance.</p>
        </section>

        <section>
          <h2>22. Grievance Officer (Mandatory IT Rules Compliance)</h2>
          <p>
            In accordance with the <strong>Information Technology (Intermediary Guidelines and Digital Media Ethics 
            Code) Rules, 2021</strong>, the Grievance Officer details are:
          </p>
          <p>
            <strong>Grievance Officer:</strong><br />
            Step and Sit LLP
          </p>
          <p>Email: <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a></p>
          <p>
            All grievances shall be acknowledged within <strong>48 hours</strong> and resolved within <strong>15 days</strong>, 
            where feasible.
          </p>
        </section>

        <section>
          <h2>23. Contact Information</h2>
          <p>
            <strong>Step and Sit LLP</strong><br />
            Email: <a href="mailto:admin@stepandsit.com">admin@stepandsit.com</a><br />
            Website: <a href="https://www.stepandsit.com" target="_blank" rel="noopener noreferrer">www.stepandsit.com</a>
          </p>
        </section>
      </div>

      <div className="legal-footer">
        <Button onClick={() => navigate('/')}>
          Back to Home
        </Button>
      </div>
    </div>
  );
}

export default TermsAndConditions;
